import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import lodashPick from 'lodash/fp/pick';

import { SR_API_ACTION_PARTNER_UPDATE_MARKETING_LISTING_INSTALLED_STATUS } from 'api/actionTypes';

import { makeListingAddressStreetDisplay } from 'common/listingHelpers';
import setNullEmptyValues from 'common/util/setNullEmptyValues';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import postWithState from 'client/actions/postWithState';

import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrModalV2, { MODAL_THEME_FIXED_FOOTER_BUTTON } from 'client/components/SrModalV2/SrModalV2';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';

const getFormFields = ({ fields }) => {
  const formFields = [
    {
      id: 'submittedForMarketingNotes',
      title: 'Write a note for the partner',
      type: 'long_text',
      properties: {
        placeholder: 'Sunroom will contact the owner for setup.',
      },
    },
  ];
  return formFields.filter(({ id }) => fields.includes(id));
};

const cleanFormValues = (fields, values) => {
  return lodashPick(fields, setNullEmptyValues(values));
};

const SrPartnerMarketListingEditFieldsModal = ({
  label,
  fields,
  dispatch,
  marketingPartnerListing,
  reloadDataTableMarketingPartnerListing,
  onClose,
}) => {
  const _onSubmit = useCallback(
    (values) => {
      dispatch(postWithState, {
        action: SR_API_ACTION_PARTNER_UPDATE_MARKETING_LISTING_INSTALLED_STATUS,
        payload: {
          marketingPartnerListingId: marketingPartnerListing.id,
          updates: cleanFormValues(fields, values),
        },
        onSuccess: () => {
          reloadDataTableMarketingPartnerListing(marketingPartnerListing);
          onClose();
        },
      });
    },
    [dispatch, fields, marketingPartnerListing, onClose, reloadDataTableMarketingPartnerListing],
  );

  return (
    <Formik
      validate={(values) => {
        const formFields = getFormFields({ values, fields });
        return createFormValidationFn(formFields)(values);
      }}
      initialValues={createInitialValues({
        formFields: getFormFields({ fields }),
        defaultValues: marketingPartnerListing,
      })}
      onSubmit={_onSubmit}
      render={(formikBag) => {
        const formFields = getFormFields({ values: formikBag.values, fields });
        return (
          <SrModalV2
            theme={MODAL_THEME_FIXED_FOOTER_BUTTON}
            buttonTheme={SR_BUTTON_THEME_ADOBE_GREY}
            onClose={onClose}
            buttonProps={[
              {
                text: 'Close',
                theme: SR_BUTTON_THEME_ADOBE_GREY,
                onClick: onClose,
              },
              {
                text: 'Save',
                theme: SR_BUTTON_THEME_ADOBE_GREEN,
                onClick: () => formikBag.submitForm(),
              },
            ]}
          >
            <SrBox padding="0 15px">
              <SrText
                font="greycliff"
                paddingBottom="15px"
                lineHeight="35px"
                size="30"
                weight="bolder"
                display="block"
              >
                {label} - {makeListingAddressStreetDisplay(marketingPartnerListing.listing)}
              </SrText>
              <SrBox marginTop="20px" />
              <Form className="form">
                {formFields.map((formField) => (
                  <SrFormikField
                    key={formField.id}
                    formField={formField}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                ))}
              </Form>
            </SrBox>
          </SrModalV2>
        );
      }}
    />
  );
};

SrPartnerMarketListingEditFieldsModal.propTypes = {
  label: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  dispatch: PropTypes.func.isRequired,
  marketingPartnerListing: PropTypes.object.isRequired,
  reloadDataTableMarketingPartnerListing: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

SrPartnerMarketListingEditFieldsModal.displayName = 'SrPartnerMarketListingEditFieldsModal';

export default SrPartnerMarketListingEditFieldsModal;
